<template>
  <FlotoDrawerForm
    ref="drawer"
    width="40%"
    class="inline-block"
    @cancel="onCancel"
    @submit="onExport"
    @show="onShow"
  >
    <template v-slot:trigger="slotData">
      <slot name="trigger" v-bind="slotData"><span /></slot>
    </template>
    <template v-slot:header>
      <h5 class="mb-0">
        {{ $t('export') }} {{ title ? $tc(title) : $tc(moduleName, 2) }}
      </h5>
    </template>
    <FlotoFormItem
      v-if="takeEmails"
      id="emails-picker"
      :label="$tc('email_plural', 2)"
      rules="required|min:1"
    >
      <FlotoTagsPicker v-model="data.emails" type="email" />
    </FlotoFormItem>
    <FlotoFormItem id="export-format-input" :label="$tc('format', 2)">
      <ExportFormat v-model="data.exportFormat" :options="formatOptions" />
    </FlotoFormItem>
    <template v-if="['pdf', 'excel', 'word'].indexOf(data.exportFormat) >= 0">
      <MCol :size="6">
        <FlotoFormItem
          id="enabled-switch"
          :label="`${$tc('password')} ${$tc('protected')}`"
        >
          <MSwitch v-model="data.enabled" size="small" class="mr-2" />
        </FlotoFormItem>
      </MCol>
      <MCol
        v-if="data.enabled"
        :size="6"
        style="padding-right: 9px"
        class="mb-2"
      >
        <PasswordInput
          v-model="data.protectedPassword"
          :validation-label="`${$tc('attachment')} ${$tc('password')}`"
          :rules="{
            required: !attachmentPassword,
          }"
          :label="`${$tc('attachment')} ${$tc('password')}`"
          :placeholder="`${$tc('attachment')} ${$tc('password')}`"
          :enabled-encryption="false"
          class="mb-1"
        >
          <template v-slot:prefix>
            <MIcon name="unlock-alt" />
          </template>
        </PasswordInput>
        <span v-if="attachmentPassword" class="md-text-xs mb-2">
          {{ $tc('attachment_password_info') }}
        </span>
      </MCol>
    </template>
    <FlotoContentLoader :loading="loading">
      <FlotoFixedView>
        <MInput
          v-model="query"
          auto-focus
          class="search-box mt-2 mb-4"
          :placeholder="$t('search')"
        >
          <template v-slot:prefix>
            <MIcon name="search" />
          </template>
        </MInput>
        <div
          :class="collapsibleColumnSelection ? 'ml-1' : 'ml-1 mb-2'"
          :style="isRtl ? { paddingRight: '20px', paddingLeft: '20px' } : {}"
        >
          <MCheckbox
            :checked="data.columns.length === availableColumns.length"
            @change="handleSelectAll"
          >
            <strong>
              {{ $tc('select') }} {{ $tc('all') }}
              {{ $tc('field', 2) }}
            </strong>
          </MCheckbox>
        </div>
        <FlotoScrollView>
          <MRow v-if="!collapsibleColumnSelection" :gutter="0" class="pr-4">
            <MCol>
              <ColumnSelectionList
                v-model="data.columns"
                :columns="availableColumns"
                :query="query"
              />
            </MCol>
          </MRow>
          <MRow v-else class="pr-4" :gutter="0">
            <MCol>
              <MCollapse
                v-if="Object.keys(columnGroups).length"
                :accordion="false"
                class="my-4"
                :default-active-key="Object.keys(columnGroups)[0]"
              >
                <MCollapsePanel
                  v-for="(c, group) in columnGroups"
                  :key="group"
                  :header="$tc(group)"
                >
                  <ColumnSelectionList
                    v-model="data.columns"
                    :collapsible-column-selection="collapsibleColumnSelection"
                    class="w-full -my-4"
                    :columns="c"
                    :all-columns="availableColumns"
                    :query="query"
                  />
                </MCollapsePanel>
              </MCollapse>
            </MCol>
          </MRow>
        </FlotoScrollView>
      </FlotoFixedView>
    </FlotoContentLoader>
    <template v-slot:actions="{ submit, hide }">
      <MButton
        id="export-btn"
        class="mx-1"
        variant="primary"
        :loading="processing"
        @click="submit"
      >
        {{ $t('export') }}
      </MButton>
      <MButton id="cancel-btn" variant="default" @click="hide">
        {{ $t('cancel') }}
      </MButton>
    </template>
  </FlotoDrawerForm>
</template>

<script>
import PasswordInput from '@components/password-input'
import GroupBy from 'lodash/groupBy'
import { searchList } from '@utils/arr'
import ExportFormat from '@components/export-format'
import { getExportAvailableColumnsApi } from '@modules/ticket/ticket-api'
import { authComputed } from '@state/modules/auth'
import { PreferenceComputed } from '@state/modules/preference'
import ColumnSelectionList from '@modules/reports/components/form-components/column-selection-list.vue'
export default {
  name: 'ExportDrawer',
  components: { ExportFormat, ColumnSelectionList, PasswordInput },
  props: {
    moduleName: { type: String, required: true },
    createFn: { type: Function, required: true },
    supportedDataFn: { type: Function, default: undefined },
    takeEmails: { type: Boolean, default: false },
    allowPdf: { type: Boolean, default: false },
    defaultSelectedColumns: {
      type: Array,
      default() {
        return []
      },
    },
    collapsibleColumnSelection: { type: Boolean, default: false },
    additionalParams: {
      type: Object,
      default() {
        return {}
      },
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    this.options = [
      { text: this.$t('csv'), value: 'csv' },
      { text: this.$t('excel'), value: 'excel' },
    ]
    return {
      availableColumns: [],
      loading: false,
      query: '',
      processing: false,
      data: {
        exportFormat: 'csv',
        columns: [],
        enabled: false,
        protectedPassword: '',
      },
    }
  },
  computed: {
    ...authComputed,
    ...PreferenceComputed,
    formatOptions() {
      return [
        ...this.options,
        ...(this.allowPdf ? [{ text: this.$t('pdf'), value: 'pdf' }] : []),
      ]
    },
    columnGroups() {
      if (this.collapsibleColumnSelection) {
        let col = this.availableColumns
        if (this.query) {
          col = searchList(this.availableColumns, this.query, 'text')
        }
        return GroupBy(col, 'groupName')
      }
      return {}
    },
  },
  methods: {
    handleSelectAll(event) {
      if (event === true) {
        const availableColumns = this.availableColumns
        const selected = availableColumns
          .map((a) => a.key)
          .filter((c) => {
            const keys = c.split('.')
            const key = keys[keys.length - 1]
            return key
          })
        this.data = {
          ...this.data,
          columns: selected,
        }
      } else {
        this.data = {
          ...this.data,
          columns: [],
        }
      }
    },
    getSupportData() {
      this.loading = true
      let fetchFn = getExportAvailableColumnsApi
      if (this.supportedDataFn) {
        fetchFn = this.supportedDataFn
      }
      fetchFn(this.moduleName, this.additionalParams).then((data) => {
        this.loading = false
        this.availableColumns = data.exportColumns
        this.getDefaultSelection(this.defaultSelectedColumns)
      })
    },
    onShow() {
      this.getSupportData()
    },
    onCancel() {
      this.data = {
        exportFormat: 'csv',
        columns: [],
        enabled: false,
        protectedPassword: '',
      }
      this.query = ''
      this.$refs.drawer.hide()
    },
    onExport() {
      if (!this.data.columns.length) {
        return this.$errorNotification({
          message: this.$t('error'),
          description: this.$t('expoert_data_validation_message'),
        })
      }
      this.processing = true
      this.createFn(this.data)
        .then(() => {
          this.processing = false
          this.onCancel()
        })
        .catch(() => {
          this.processing = false
        })
        .finally(() => {
          this.processing = false
        })
    },
    getDefaultSelection(columns) {
      const availableColumns = this.availableColumns
      const selected = availableColumns
        .map((a) => a.key)
        .filter((c) => {
          const keys = c.split('.')
          const key = keys[keys.length - 1]
          return columns.indexOf(key) >= 0
        })
      this.data = {
        ...this.data,
        columns: selected,
      }
    },
  },
}
</script>
